/**
 * Resolve the URL for an `sld-api/*` PHP endpoint that works in both
 * development (Vite dev server with proxy rewrite) and production
 * (deployed alongside the rest of the Solar Proof project under
 * /solarproof/sld/).
 *
 * Why this exists:
 *   In production the SLD bundle is served from a SUBDIRECTORY of the
 *   SP project (typically /solarproof/sld/dist/), and an absolute fetch
 *   to `/sld-api/...` resolves to the document root — where there is
 *   no `/sld-api/` directory, only `/solarproof/sld-api/`. That's the
 *   404 source for PDF export when a user hits this against the
 *   deployed build. In dev the Vite proxy in vite.config.ts handles
 *   the rewrite so the same relative path works there too.
 *
 * Strategy:
 *   1. In Vite dev (import.meta.env.DEV true): use the relative path
 *      `/sld-api/<path>`. The vite.config.ts proxy intercepts it and
 *      rewrites to http://localhost/solarproof/sld-api/<path>.
 *   2. In production: walk up window.location.pathname looking for
 *      `/sld/` segment. Everything before that segment is the SP root
 *      (e.g. `/solarproof`). Append `/sld-api/<path>` to get the
 *      correct sibling-of-sld endpoint URL.
 *   3. Fallback (no `/sld/` segment in URL — bundle mounted at host
 *      root): use `/sld-api/<path>` directly.
 *
 * If you're seeing 404s in dev mode despite the proxy:
 *   - Confirm Apache is reachable on port 80 by opening
 *     http://localhost/solarproof/sld-api/pdf.php directly. You should
 *     see JSON {"error":"Method Not Allowed"} (because GET isn't
 *     POST), which proves the file is reachable.
 *   - If Apache is on a different port, set VITE_SLD_API_BASE in
 *     .env.local to override the proxy rewrite (e.g.
 *     VITE_SLD_API_BASE=http://localhost:8080/solarproof) and restart
 *     `npm run dev`. The override skips the proxy entirely.
 */
export function sldApiUrl(path: string): string {
  // Strip any leading slash on the caller's path so we control
  // joining and don't accidentally produce `//path`.
  const clean = path.replace(/^\/+/, '');

  // Vite dev path. If the user has set VITE_SLD_API_BASE (Apache on
  // a non-default port, remote dev box, etc.), use that absolute URL
  // and bypass the proxy. Otherwise rely on the proxy in vite.config
  // which handles the /solarproof prefix.
  if (import.meta.env.DEV) {
    const override = import.meta.env.VITE_SLD_API_BASE as string | undefined;
    if (override) {
      return `${override.replace(/\/+$/, '')}/sld-api/${clean}`;
    }
    return `/sld-api/${clean}`;
  }

  // Production — derive the SP root from the current pathname. The
  // deployed bundle lives at <ROOT>/sld/... and sld-api lives at
  // <ROOT>/sld-api/..., so we want everything before `/sld/`.
  const here = typeof window !== 'undefined' ? window.location.pathname : '';
  const sldSegmentIdx = here.indexOf('/sld/');
  if (sldSegmentIdx >= 0) {
    const root = here.slice(0, sldSegmentIdx);
    return `${root}/sld-api/${clean}`;
  }

  // No /sld/ segment found — fall back to the absolute path. Either
  // the bundle is mounted at the host root (legitimate) or the URL
  // structure is non-standard (in which case the caller will see a
  // 404 with this URL surfaced in the error message).
  return `/sld-api/${clean}`;
}
